import React, { useState, useEffect, useRef } from 'react';
import { useInView } from "motion/react";
import { Users, Eye, Heart, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { mockRobloxGames } from '../mock';

import Counter from './utils/Counter';
import './Contributions.css';

const Contributions = () => {
  const [gamesData, setGamesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllGames();
  }, []);

  // CORS Proxy para contornar restrições do navegador
  const CORS_PROXY = 'https://corsproxy.io/?';
  
  // Fetch Universe ID from Place ID
  const getUniverseId = async (placeId) => {
    try {
      const url = `https://apis.roblox.com/universes/v1/places/${placeId}/universe`;
      const response = await fetch(CORS_PROXY + encodeURIComponent(url));
      if (!response.ok) throw new Error('Failed to get universe ID');
      const data = await response.json();
      return data.universeId;
    } catch (err) {
      console.error(`Error getting universe ID for ${placeId}:`, err);
      return null;
    }
  };

  // Fetch game details from Roblox API
  const getGameDetails = async (universeId) => {
    try {
      const url = `https://games.roblox.com/v1/games?universeIds=${universeId}`;
      const response = await fetch(CORS_PROXY + encodeURIComponent(url));
      if (!response.ok) throw new Error('Failed to get game details');
      const data = await response.json();
      if (data.data && data.data.length > 0) {
        return data.data[0];
      }
      return null;
    } catch (err) {
      console.error(`Error getting game details for universe ${universeId}:`, err);
      return null;
    }
  };

  // Fetch game thumbnail
  const getGameThumbnail = async (universeId) => {
    try {
      const url = `https://thumbnails.roblox.com/v1/games/icons?universeIds=${universeId}&size=512x512&format=Png&isCircular=false`;
      const response = await fetch(CORS_PROXY + encodeURIComponent(url));
      if (!response.ok) throw new Error('Failed to get thumbnail');
      const data = await response.json();
      if (data.data && data.data.length > 0) {
        return data.data[0].imageUrl;
      }
      return 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&q=80';
    } catch (err) {
      console.error(`Error getting thumbnail for universe ${universeId}:`, err);
      return 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&q=80';
    }
  };

  // Fetch data for a single game
  const fetchGameData = async (placeId) => {
    try {
      // Step 1: Get Universe ID
      const universeId = await getUniverseId(placeId);
      if (!universeId) {
        console.error(`No universe ID found for place ${placeId}`);
        return null;
      }

      // Step 2: Get game details
      const gameDetails = await getGameDetails(universeId);
      if (!gameDetails) {
        console.error(`No game details found for universe ${universeId}`);
        return null;
      }

      // Step 3: Get thumbnail
      const thumbnail = await getGameThumbnail(universeId);

      return {
        placeId: placeId,
        universeId: String(universeId),
        name: gameDetails.name || 'Unknown Game',
        description: gameDetails.description || '',
        thumbnail: thumbnail,
        playing: gameDetails.playing || 0,
        visits: gameDetails.visits || 0,
        favorites: gameDetails.favoritedCount || 0,
        created: gameDetails.created || '',
        updated: gameDetails.updated || ''
      };
    } catch (err) {
      console.error(`Error fetching data for ${placeId}:`, err);
      return null;
    }
  };

  const fetchAllGames = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch stats for all games sequentially (to avoid rate limits)
      const results = [];
      for (const placeId of mockRobloxGames) {
        const gameData = await fetchGameData(placeId);
        if (gameData) {
          results.push(gameData);
        }
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      if (results.length === 0) {
        throw new Error('Failed to load any game statistics. Please check your Place IDs.');
      }
      
      setGamesData(results);
    } catch (err) {
      console.error('Error fetching games:', err);
      setError(err.message || 'Failed to load game statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getStatsForGame = (gameStats) => {
    return [
      {
        icon: Users,
        label: 'Playing Now',
        value: gameStats.playing,
        color: '#10b981'
      },
      {
        icon: Eye,
        label: 'Total Visits',
        value: gameStats.visits,
        color: '#3b82f6'
      },
      {
        icon: Heart,
        label: 'Favorites',
        value: gameStats.favorites,
        color: '#ef4444'
      }
    ];
  };

  if (loading) {
    return (
      <section id="contributions" className="section contributions-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Contributions</h2>
            <p className="section-subtitle">Games to which I contribute or have contributed.</p>
          </div>
          <div className="loading-container">
            <Loader2 className="loading-spinner" size={48} />
            <p className="loading-text">Fetching real-time game statistics...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="contributions" className="section contributions-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Contributions</h2>
            <p className="section-subtitle">Games to which I contribute or have contributed.</p>
          </div>
          <div className="error-container">
            <AlertCircle size={48} className="error-icon" />
            <p className="error-text">{error}</p>
            <button onClick={fetchAllGames} className="retry-button">
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contributions" className="section contributions-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Contributions</h2>
          <p className="section-subtitle">Games to which I contribute or have contributed.</p>
        </div>
        
        <div className="games-grid">
          {gamesData.map((gameStats, gameIndex) => {
            const stats = getStatsForGame(gameStats);
            return (
              <div key={gameIndex} className="contribution-card">
                <div className="game-thumbnail">
                  <img src={gameStats.thumbnail} alt={gameStats.name} />
                  <div className="live-badge">
                    <span className="live-dot"></span>
                    Live
                  </div>
                </div>
                
                <div className="game-info">
                  <div className="game-header">
                    <div>
                      <h3 className="game-title">{gameStats.name}</h3>
                      <p className="game-description">
                        {gameStats.description.length > 150 
                          ? gameStats.description.substring(0, 150) + '...' 
                          : gameStats.description}
                      </p>
                    </div>
                    <a 
                      href={`https://www.roblox.com/games/${gameStats.placeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="game-link"
                    >
                      <ExternalLink size={20} />
                    </a>
                  </div>
                  
                  <div className="stats-grid">
                    {stats.map((stat, index) => (
                      <div key={index} className="stat-item">
                        <div className="stat-icon" style={{ color: stat.color }}>
                          <stat.icon size={20} />
                        </div>
                        <div className="stat-content">
                          <div className="stat-value"><Counter value={stat.value}/> </div> 
                          <div className="stat-label">{stat.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <p className="stats-note">
          <span className="note-icon">ℹ️</span>
          Statistics are fetched in real-time from Roblox API
        </p>
      </div>
    </section>
  );
};

export default Contributions;